export const PREDOMINANT_DEGREE_OPTIONS = {
  NOT_CLASSIFIED: 'Not classified',
  PREDOMINANTLY_CERTIFICATE_DEGREE_GRANTING: 'Predominantly certificate-degree granting',
  PREDOMINANTLY_ASSOCIATES_DEGREE_GRANTING: "Predominantly associate's-degree granting",
  PREDOMINANTLY_BACHELORS_DEGREE_GRANTING: "Predominantly bachelor's-degree granting",
  ENTIRELY_GRADUATE_DEGREE_GRANTING: 'Entirely graduate-degree granting',
} as const;

export type PredominantDegree =
  (typeof PREDOMINANT_DEGREE_OPTIONS)[keyof typeof PREDOMINANT_DEGREE_OPTIONS];

export type PredominantDegreeCode =
  | 'not_classified'
  | 'certificate'
  | 'associates'
  | 'bachelors'
  | 'graduate';

export const SCORECARD_REGION_OPTIONS = {
  U_S_SERVICE_SCHOOLS: 'U.S. Service Schools',
  NEW_ENGLAND: 'New England',
  MID_EAST: 'Mid East',
  GREAT_LAKES: 'Great Lakes',
  PLAINS: 'Plains',
  SOUTHEAST: 'Southeast',
  SOUTHWEST: 'Southwest',
} as const;

export type ScorecardRegion =
  (typeof SCORECARD_REGION_OPTIONS)[keyof typeof SCORECARD_REGION_OPTIONS];

export const BACKRS_REGION_OPTIONS = {
  NORTHEAST: 'Northeast',
  MIDWEST: 'Midwest',
  SOUTHEAST: 'Southeast',
  WEST: 'West',
} as const;

export type BackrsRegion = (typeof BACKRS_REGION_OPTIONS)[keyof typeof BACKRS_REGION_OPTIONS];

export type College = Readonly<{
  id: number;
  academic_profile_id: number;
  ipeds_id: number;
  school_name: string;
  url: string;
  predominant_degree?: PredominantDegree;
  predominant_degree_code?: PredominantDegreeCode;
  carnegie_classification?: string;
  endowment?: number;
  city?: string;
  state?: string;
  admission_rate?: number;
  satvr25?: number;
  satvr50?: number;
  satvr75?: number;

  satmt25?: number;
  satmt50?: number;
  satmt75?: number;

  actcm50?: number;
  actcm25?: number;
  actcm75?: number;

  latitude?: number;
  longitude?: number;
  median_grad_debt?: number;
  grad_threshold_6_years?: number;
  graduation_rate_6yr?: number;
  graduation_rate_8yr?: number;
  retention_rate?: number;
  graduation_rate_pell_6yr?: number;
  graduation_rate_pell_8yr?: number;
  graduation_rate_lowinc_8yr?: number;
  median_earnings_10yr?: number;
  median_earnings_level?: 'high' | 'medium' | 'low';
  undergrad_enrollment?: number;
  student_faculty_ratio?: number;
  median_completion_rate?: number;
  median_cost?: number;
  ranking?: number;
  scorecard_region?: ScorecardRegion;
  backrs_region?: BackrsRegion;
  gpa_25?: number;
  gpa_50?: number;
  gpa_75?: number;
  open_admission?: boolean;
  created_at: Date;
  updated_at: Date;
  category?: string;
  type?: string;
  setting?: string;
  early_decision?: boolean;
  early_decision_deadline?: Date;
  application_deadline?: Date;
  application_url?: string;
  gpa_mean?: number;
  gpa_mean_weighted?: number;
  net_price_calculator_url?: string;
  net_price_income_level_0_30000?: number;
  net_price_income_level_30001_48000?: number;
  net_price_income_level_48001_75000?: number;
  net_price_income_level_75001_110000?: number;
  net_price_income_level_110001_plus?: number;
  tuition_in_state?: number;
  tuition_out_of_state?: number;
  room_and_board?: number;
  fees?: number;
  average_financial_aid?: number;

  scoop_best_known_for?: string;
  scoop_vibe_check?: string;
  scoop_opportunities_networks?: string;
  description?: string;

  coursework_rigor?: number;
  extracurricular_involvement?: number;

  section_who_gets_in_summary?: string;
  section_the_cost_summary?: string;

  selectivity?: number;

  logo_media_id?: number | null;
  logo_media_url?: string | null;
  banner_media_id?: number | null;
  banner_media_url?: string | null;

  scorecardRoi?: {
    cohort_cut?: string;
    years_enrolled?: number | null;
    net_price?: number | null;
    net_price_source_column?: string | null;
    dollar_base?: number | null;
    earnings_total?: number | null;
    cost_total?: number | null;
    roi_10_year?: number | null;
    roi_15_year?: number | null;
    roi_20_year?: number | null;
    roi_30_year?: number | null;
    roi_40_year?: number | null;
  } | null;

  chettyData?: {
    pct_from_bottom_20?: number;
    pct_to_top_20?: number;
    pct_to_top_1?: number;
    mobility_rate?: number;
    median_parent_income?: number;
    median_child_income?: number;
    kq5_cond_parq1?: number;
    success_rate_level?: 'high' | 'medium' | 'low';
  } | null;
}>;
